import React from 'react';
import { TypeList, TypeListItem, spacer12, spacer20, Type, VisuallyHidden } from '@spotify-internal/encore-web';
import styled from 'styled-components';

import { DocumentLink } from 'lib/sanity/types';
import { useTranslation } from 'providers/translations/TranslationProvider';
import IconChevron from 'components/IconChevron';
import Ellipsis from 'components/Ellipsis';
import { ArticleLink } from 'components/Links';
import { UbiHomePage } from 'utils/ubi/pages/useUbiHome';
import { Whats } from 'utils/metrics';
import useShouldUseSemanticSearch from 'utils/semantic';

interface SearchResultsProps {
  searchResults: DocumentLink[];
  ubi: UbiHomePage;
}

const Container = styled.div`
  padding-top: ${spacer12};
`;

const StyledArticleLink = styled(ArticleLink)`
  display: flex;
  flex: 1;
  justify-content: space-between;
  width: 100%;
`;

const StyledListItem = styled(TypeListItem)`
  height: 24px;
  padding: ${spacer20} 0;
  display: flex;
  align-items: center;
`;

const SearchResults = ({ searchResults, ubi }: SearchResultsProps) => {
  const { t } = useTranslation();
  const shouldUseSemanticSearch = useShouldUseSemanticSearch();
  const emptyResultCopy = shouldUseSemanticSearch
    ? t('semantic_search_results_no_results_description')
    : t('search_results_no_results_description');

  return (
    <Container aria-label={t('search_results')}>
      {!searchResults.length && (
        <Type as="span" aria-live="polite" aria-atomic="true" data-testid="noResultsMessage" variant="ballad">
          <em>{emptyResultCopy}</em>
        </Type>
      )}

      {searchResults.length > 0 && (
        <>
          <VisuallyHidden data-testid="ariaMessage" role="status" aria-live="polite" aria-atomic="true">
            {searchResults.length} {t('search_results_found')}
          </VisuallyHidden>
          <TypeList data-testid="resultList" condensedAll listStyleReset>
            {searchResults.map((article: DocumentLink, index: number) => (
              <StyledListItem key={article.slug} role="listitem" variant="ballad" condensed listStyleReset>
                <StyledArticleLink
                  data-testid={`${article.title}-Link`}
                  slug={article.slug}
                  eventDefinition={{
                    category: 'search-dropdown',
                    action: `click-index-${index + 1}`,
                    label: article.slug,
                  }}
                  ubiFactory={ubi.logSearchResult(article.slug, index)}
                  sendMetrics={{
                    what: Whats.SEARCH_DROPDOWN,
                    tags: {
                      slug: article.slug,
                      engine: 'sanity',
                    },
                  }}
                >
                  <Ellipsis variant="ballad">{article.title}</Ellipsis>
                  <IconChevron />
                </StyledArticleLink>
              </StyledListItem>
            ))}
          </TypeList>
        </>
      )}
    </Container>
  );
};

export default SearchResults;
